import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  adminMe, clearToken, adminDashboard, adminListApplications, adminPatchApplication,
  adminAssignCertificate, adminPatchPackage, adminExportUrl, getToken,
} from "@/lib/adminApi";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "applications", label: "Applications" },
  { id: "packages", label: "Packages" },
  { id: "live", label: "Live Screen" },
];

const STATUSES = ["pending-review", "approved", "declined", "reserved-paid", "completed"];
const ASSIGNEES = ["", "jalimo", "moraa", "toriano"];

function Sidebar({ user, active, setActive, onLogout }) {
  return (
    <aside className="hidden md:flex flex-col w-64 bg-ink-2 border-r border-hair p-6 min-h-screen">
      <div className="flex items-center gap-2 mb-8">
        <span className="wolf-mark gold-foil text-2xl">TS</span>
        <div>
          <div className="text-hair text-gold">Wolfkeeper</div>
          <div className="text-hair text-bone/60">Admin</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setActive(t.id)} className={`w-full text-left px-4 py-3 rounded-lg text-sm ${active === t.id ? "bg-gold/10 text-gold border border-gold/30" : "text-bone/75 hover:bg-white/5"}`} data-testid={`admin-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </nav>
      <div className="border-t border-hair pt-4 mt-6">
        <div className="text-hair text-bone/50">{user?.name} · {user?.role}</div>
        <button onClick={onLogout} className="text-hair text-bone/60 hover:text-blood mt-2" data-testid="admin-logout">Sign out</button>
      </div>
    </aside>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => { adminDashboard().then(setData).catch(() => toast.error("Failed to load")); }, []);
  if (!data) return <div className="p-8 text-bone/60">Loading…</div>;
  const t = data.totals;
  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="admin-overview">
      <div>
        <div className="text-eyebrow text-gold">Overview</div>
        <h2 className="font-display text-4xl mt-2">Dashboard.</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ["Applications", t.applications], ["Pending", t.pending], ["Approved", t.approved],
          ["Declined", t.declined], ["Reserved", t.reserved], ["Revenue", `$${(t.revenue || 0).toLocaleString()}`],
        ].map(([l, v]) => (
          <div key={l} className="bg-ink-2 border border-hair rounded-xl p-4">
            <div className="text-hair text-bone/50">{l}</div>
            <div className="stat-num text-3xl mt-2 text-bone">{v}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-ink-2 border border-hair rounded-xl p-6">
          <div className="text-hair text-gold mb-4">Revenue by Package</div>
          {data.by_package.length === 0 && <div className="text-bone/50 text-sm">No paid reservations yet.</div>}
          <ul className="space-y-2">
            {data.by_package.map((r) => (
              <li key={r.slug} className="flex justify-between text-bone/80"><span>{r.slug}</span><span className="stat-num">${r.amount.toLocaleString()}</span></li>
            ))}
          </ul>
        </div>
        <div className="bg-ink-2 border border-hair rounded-xl p-6">
          <div className="text-hair text-gold mb-4">Revenue by City</div>
          {data.by_city.length === 0 && <div className="text-bone/50 text-sm">No paid reservations yet.</div>}
          <ul className="space-y-2">
            {data.by_city.map((r) => (
              <li key={r.city} className="flex justify-between text-bone/80"><span>{r.city}</span><span className="stat-num">${r.amount.toLocaleString()}</span></li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [filters, setFilters] = useState({ status: "", package_slug: "", assignee: "", source: "" });
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => adminListApplications(filters).then(setApps).catch(() => toast.error("Failed to load")), [filters]);
  useEffect(() => { load(); }, [load]);

  const patch = async (num, body) => {
    try {
      const upd = await adminPatchApplication(num, body);
      setApps((prev) => prev.map((a) => a.application_number === num ? upd : a));
      if (selected?.application_number === num) setSelected(upd);
      toast.success("Saved");
    } catch { toast.error("Failed"); }
  };

  const assignCert = async (num) => {
    try {
      const { certificate_number } = await adminAssignCertificate(num);
      toast.success(`Certificate: ${certificate_number}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const download = () => {
    const url = adminExportUrl();
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "wolfkeeper_applications.csv";
        a.click();
      });
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-applications">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-eyebrow text-gold">Applications</div>
          <h2 className="font-display text-4xl mt-2">{apps.length} leads</h2>
        </div>
        <button onClick={download} className="btn-gold" data-testid="admin-export-csv">Export CSV</button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="field"><label>Status</label>
          <select value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})} data-testid="filter-status">
            <option value="">All</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="field"><label>Assignee</label>
          <select value={filters.assignee} onChange={(e) => setFilters({...filters, assignee: e.target.value})} data-testid="filter-assignee">
            {ASSIGNEES.map((s) => <option key={s || "all"} value={s}>{s || "All"}</option>)}
          </select>
        </div>
        <div className="field"><label>Package</label>
          <input value={filters.package_slug} onChange={(e) => setFilters({...filters, package_slug: e.target.value})} placeholder="e.g. chicago-2026" data-testid="filter-package"/>
        </div>
        <div className="field"><label>Source</label>
          <select value={filters.source} onChange={(e) => setFilters({...filters, source: e.target.value})} data-testid="filter-source">
            <option value="">All</option><option>website</option><option>tiktok-live</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-ink-2 border border-hair rounded-xl overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left text-hair text-bone/60">
              <th className="px-3 py-3">App #</th><th className="px-3 py-3">Name</th><th className="px-3 py-3">Package</th>
              <th className="px-3 py-3">Bid</th><th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Assignee</th><th className="px-3 py-3">Source</th>
              <th className="px-3 py-3">Cert</th><th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {apps.map((a) => (
              <tr key={a.application_number} className="border-t border-hair hover:bg-white/[0.02]" data-testid={`app-row-${a.application_number}`}>
                <td className="px-3 py-3 font-mono text-gold">{a.application_number}</td>
                <td className="px-3 py-3 text-bone/90">{a.name}<div className="text-hair text-bone/50">{a.email}</div></td>
                <td className="px-3 py-3">{a.package_slug}</td>
                <td className="px-3 py-3 stat-num">{a.bid_amount || "—"}</td>
                <td className="px-3 py-3">
                  <select value={a.status || ""} onChange={(e) => patch(a.application_number, {status: e.target.value})} className="bg-transparent border border-hair rounded px-2 py-1 text-xs" data-testid={`status-${a.application_number}`}>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3">
                  <select value={a.assignee || ""} onChange={(e) => patch(a.application_number, {assignee: e.target.value})} className="bg-transparent border border-hair rounded px-2 py-1 text-xs">
                    {ASSIGNEES.map((s) => <option key={s || "u"} value={s}>{s || "Unassigned"}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3 text-xs">{a.source}</td>
                <td className="px-3 py-3 font-mono text-xs">{a.certificate_number || <button onClick={() => assignCert(a.application_number)} className="text-gold underline" data-testid={`cert-${a.application_number}`}>Assign</button>}</td>
                <td className="px-3 py-3"><button onClick={() => setSelected(a)} className="text-hair text-gold" data-testid={`open-${a.application_number}`}>Open →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 bg-black/60" onClick={() => setSelected(null)} data-testid="app-drawer">
          <div className="absolute right-0 top-0 bottom-0 w-full md:w-[560px] bg-ink border-l border-hair overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} className="float-right text-bone/60">✕</button>
            <div className="text-hair text-gold">{selected.application_number}</div>
            <h3 className="font-display text-2xl mt-1">{selected.name}</h3>
            <div className="text-hair text-bone/60">{selected.package_slug} · {selected.status} · {selected.source}</div>

            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div><div className="text-hair text-bone/50">Email</div><div>{selected.email}</div></div>
              <div><div className="text-hair text-bone/50">Phone</div><div>{selected.phone}</div></div>
              <div><div className="text-hair text-bone/50">WhatsApp</div><div>{selected.whatsapp || "—"}</div></div>
              <div><div className="text-hair text-bone/50">City / Country</div><div>{selected.city}, {selected.country}</div></div>
              <div><div className="text-hair text-bone/50">TikTok</div><div>{selected.tiktok_username || "—"}</div></div>
              <div><div className="text-hair text-bone/50">Heard from</div><div>{selected.heard_from || "—"}</div></div>
              <div><div className="text-hair text-bone/50">Dog</div><div>{selected.dog_name} · {selected.breed} · {selected.dog_age} · {selected.dog_sex} · {selected.dog_weight}</div></div>
              <div><div className="text-hair text-bone/50">Bid</div><div className="stat-num">{selected.bid_amount || "—"}</div></div>
              <div className="col-span-2"><div className="text-hair text-bone/50">Challenges</div><div>{selected.challenges || "—"}</div></div>
              <div className="col-span-2"><div className="text-hair text-bone/50">Bite / medical</div><div>{selected.bite_history || "None"} · {selected.medical || "None"}</div></div>
              <div><div className="text-hair text-bone/50">Merch size</div>
                <input defaultValue={selected.merch_size || ""} onBlur={(e) => patch(selected.application_number, {merch_size: e.target.value})} className="bg-transparent border border-hair rounded px-2 py-1 text-xs" data-testid="drawer-merch"/>
              </div>
              <div><div className="text-hair text-bone/50">Collar</div>
                <input defaultValue={selected.collar_notes || ""} onBlur={(e) => patch(selected.application_number, {collar_notes: e.target.value})} className="bg-transparent border border-hair rounded px-2 py-1 text-xs" data-testid="drawer-collar"/>
              </div>
              <div><div className="text-hair text-bone/50">Leash</div>
                <input defaultValue={selected.leash_notes || ""} onBlur={(e) => patch(selected.application_number, {leash_notes: e.target.value})} className="bg-transparent border border-hair rounded px-2 py-1 text-xs" data-testid="drawer-leash"/>
              </div>
              <div><div className="text-hair text-bone/50">Deposit</div>
                <input type="number" defaultValue={selected.deposit || ""} onBlur={(e) => patch(selected.application_number, {deposit: parseFloat(e.target.value || "0")})} className="bg-transparent border border-hair rounded px-2 py-1 text-xs" data-testid="drawer-deposit"/>
              </div>
              <div><div className="text-hair text-bone/50">Balance</div>
                <input type="number" defaultValue={selected.balance || ""} onBlur={(e) => patch(selected.application_number, {balance: parseFloat(e.target.value || "0")})} className="bg-transparent border border-hair rounded px-2 py-1 text-xs" data-testid="drawer-balance"/>
              </div>
              <div className="col-span-2"><div className="text-hair text-bone/50">Payment link</div>
                <input defaultValue={selected.payment_link || ""} onBlur={(e) => patch(selected.application_number, {payment_link: e.target.value})} placeholder="https://…" className="w-full bg-transparent border border-hair rounded px-2 py-1 text-xs" data-testid="drawer-payment"/>
              </div>
              <div className="col-span-2">
                <div className="text-hair text-bone/50 mb-2">Follow-up notes ({(selected.follow_up_notes || []).length})</div>
                <div className="space-y-2 max-h-48 overflow-y-auto mb-2">
                  {(selected.follow_up_notes || []).map((n, i) => (
                    <div key={`${n.at}-${i}`} className="text-xs bg-ink-2 rounded p-2">
                      <div className="text-hair text-bone/50 mb-0.5">{n.by} · {new Date(n.at).toLocaleString()}</div>
                      <div>{n.text}</div>
                    </div>
                  ))}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); const v = e.target.note.value.trim(); if (v) { patch(selected.application_number, {add_note: v}); e.target.reset(); } }}>
                  <input name="note" placeholder="Add follow-up note…" className="w-full bg-transparent border border-hair rounded px-2 py-2 text-xs" data-testid="drawer-note-input"/>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PackagesTab() {
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => { adminDashboard().then((d) => setPkgs(d.packages)).catch(() => toast.error("Failed")); }, []);

  const save = async (slug, patch) => {
    try {
      const upd = await adminPatchPackage(slug, patch);
      setPkgs((prev) => prev.map((p) => p.slug === slug ? upd : p));
      toast.success("Package saved");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-4" data-testid="admin-packages">
      <div><div className="text-eyebrow text-gold">Packages</div><h2 className="font-display text-4xl mt-2">Edit & Manage</h2></div>
      <div className="space-y-3">
        {pkgs.map((p) => (
          <PackageRow key={p.slug} pkg={p} onSave={save} />
        ))}
      </div>
    </div>
  );
}

function PackageRow({ pkg, onSave }) {
  const [form, setForm] = useState({ title: pkg.title, window_label: pkg.window_label, window_end: pkg.window_end?.slice(0, 10), opening_bid: pkg.opening_bid, price_label: pkg.price_label || "", total_slots: pkg.total_slots, remaining_slots: pkg.remaining_slots });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? parseInt(e.target.value, 10) || 0 : e.target.value });
  return (
    <div className="bg-ink-2 border border-hair rounded-xl p-5" data-testid={`pkg-row-${pkg.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <div><div className="text-hair text-gold">{pkg.slug}</div><div className="font-display text-xl">{pkg.title}</div></div>
        <div className="flex gap-2">
          {pkg.has_capacity && pkg.remaining_slots > 0 && (
            <button onClick={() => onSave(pkg.slug, { sold_out: true })} className="text-hair text-blood border border-blood/40 rounded px-3 py-1.5 text-xs" data-testid={`sold-out-${pkg.slug}`}>Mark Sold Out</button>
          )}
          <button onClick={() => onSave(pkg.slug, { ...form, window_end: form.window_end ? `${form.window_end}T23:59:59+00:00` : pkg.window_end })} className="btn-gold text-xs" data-testid={`save-${pkg.slug}`}>Save</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div className="field"><label>Title</label><input value={form.title} onChange={upd("title")}/></div>
        <div className="field"><label>Window label</label><input value={form.window_label} onChange={upd("window_label")}/></div>
        <div className="field"><label>Window end</label><input type="date" value={form.window_end} onChange={upd("window_end")}/></div>
        <div className="field"><label>Opening bid ($)</label><input type="number" value={form.opening_bid} onChange={upd("opening_bid")}/></div>
        <div className="field"><label>Price label</label><input value={form.price_label} onChange={upd("price_label")}/></div>
        <div className="field"><label>Total capacity</label><input type="number" value={form.total_slots} onChange={upd("total_slots")}/></div>
        <div className="field"><label>Remaining</label><input type="number" value={form.remaining_slots} onChange={upd("remaining_slots")}/></div>
      </div>
    </div>
  );
}

function LiveTab({ pkgs }) {
  const [slug, setSlug] = useState("chicago-2026");
  return (
    <div className="p-6 md:p-10">
      <div className="text-eyebrow text-gold">Live Broadcast Screen</div>
      <h2 className="font-display text-4xl mt-2">Auction Room.</h2>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <select value={slug} onChange={(e) => setSlug(e.target.value)} className="bg-ink-2 border border-hair rounded px-3 py-2 text-sm" data-testid="live-package-select">
          {pkgs.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
        </select>
        <Link to={`/admin/live/${slug}`} target="_blank" rel="noopener" className="btn-gold" data-testid="live-open-fullscreen">Open Broadcast Screen ↗</Link>
      </div>
      <p className="text-bone/60 text-sm mt-4">Opens the full-screen live screen in a new tab — designed to be shown on stream. Auto-refreshes every 5 seconds.</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [active, setActive] = useState("overview");
  const [pkgs, setPkgs] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    document.title = "Admin · Wolfkeeper";
    adminMe().then(setUser).catch(() => nav("/admin/login"));
    adminDashboard().then((d) => setPkgs(d.packages)).catch(() => {});
  }, [nav]);

  const logout = () => { clearToken(); nav("/admin/login"); };
  if (!user) return <div className="min-h-screen bg-ink text-bone grid place-items-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-ink text-bone flex" data-testid="admin-dashboard">
      <Sidebar user={user} active={active} setActive={setActive} onLogout={logout} />
      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden bg-ink-2 border-b border-hair p-4 flex items-center justify-between">
          <div className="wolf-mark gold-foil text-xl">TS · Admin</div>
          <button onClick={logout} className="text-hair text-bone/60">Sign out</button>
        </div>
        <div className="md:hidden flex overflow-x-auto border-b border-hair">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setActive(t.id)} className={`px-4 py-3 whitespace-nowrap text-sm ${active === t.id ? "text-gold border-b-2 border-gold" : "text-bone/60"}`} data-testid={`mobile-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
        {active === "overview" && <OverviewTab />}
        {active === "applications" && <ApplicationsTab />}
        {active === "packages" && <PackagesTab />}
        {active === "live" && <LiveTab pkgs={pkgs} />}
      </main>
    </div>
  );
}
